import React, { useState, useEffect } from 'react';
import { Button, Modal, Input } from '@/components/ui';
import type { MaintenanceContract, ContractStatus, PackageType } from '../types/contracts.types';
import { useMaintenanceContracts } from '../hooks/useMaintenanceContracts';
import { useProjects } from '@/modules/projects/shared/hooks/useProjects';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/core/auth/AuthContext';
import { toInputDate } from '@/utils/format';
import { contractStatusMap, packageTypeMap } from '@/utils/status';

interface ContractFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract?: MaintenanceContract | null;
  companyId: string;
  projectId?: string;
  defaultType?: 'construction' | 'maintenance';
}

export function ContractFormModal({ isOpen, onClose, contract, companyId, projectId, defaultType = 'construction' }: ContractFormModalProps) {
  const { profile } = useAuth(); // Assuming useAuth is available if needed, but let's check if we need it
  const { toast } = useToast();
  const isEdit = !!contract;
  const { createContract, updateContract, isCreating, isUpdating } = useMaintenanceContracts(companyId);
  const { projects = [] } = useProjects(companyId);

  const [formData, setFormData] = useState<Partial<MaintenanceContract>>({
    contract_code: '',
    contract_name: '',
    project_id: '',
    package_type: 'standard',
    contract_type: defaultType,
    status_code: 'draft',
    contract_value: 0,
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
    description: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (contract) {
        setFormData({
          ...contract,
          start_date: toInputDate(contract.start_date),
          end_date: toInputDate(contract.end_date)
        });
      } else {
        setFormData({
          contract_code: '',
          contract_name: '',
          project_id: projectId || '',
          package_type: 'standard',
          contract_type: defaultType,
          status_code: 'draft',
          contract_value: 0,
          start_date: new Date().toISOString().split('T')[0],
          end_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
          description: '',
          company_id: companyId,
        });
      }
    }
  }, [isOpen, contract, companyId, defaultType, projectId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.project_id) {
      toast.error('Vui lòng chọn dự án cho hợp đồng');
      return;
    }
    try {
      const { project, ...submitData } = formData;
      if (isEdit && contract) {
        await updateContract({ id: contract.id, updates: submitData });
      } else {
        await createContract({ ...submitData, company_id: companyId });
      }
      onClose();
      toast.success(isEdit ? 'Cập nhật hợp đồng thành công' : 'Tạo hợp đồng mới thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Cập nhật Hợp đồng' : 'Tạo Hợp đồng mới'} size="xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Mã hợp đồng *"
            required
            value={formData.contract_code || ''}
            onChange={(e) => setFormData({ ...formData, contract_code: e.target.value })}
            placeholder="VD: CONT-2026-MNT-001"
          />
          <Input
            label="Tên hợp đồng *"
            required
            value={formData.contract_name || ''}
            onChange={(e) => setFormData({ ...formData, contract_name: e.target.value })}
            placeholder="VD: Bảo dưỡng cảnh quan định kỳ - Giai đoạn 1"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Dự án áp dụng *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.project_id || ''}
              onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
            >
              <option value="">-- Chọn dự án --</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Loại hợp đồng *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.contract_type || 'construction'}
              onChange={(e) => setFormData({ ...formData, contract_type: e.target.value as any })}
            >
              <option value="construction">Thi công</option>
              <option value="maintenance">Bảo dưỡng</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Gói dịch vụ</label>
            <select
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.package_type || 'standard'}
              onChange={(e) => setFormData({ ...formData, package_type: e.target.value as PackageType })}
            >
              {Object.entries(packageTypeMap).map(([val, cfg]) => (
                <option key={val} value={val}>{cfg.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Ngày bắt đầu *"
            type="date"
            lang="vi"
            required
            value={formData.start_date || ''}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
          />
          <Input
            label="Ngày kết thúc *"
            type="date"
            lang="vi"
            required
            value={formData.end_date || ''}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Giá trị hợp đồng (VNĐ)"
            type="number"
            value={formData.contract_value || ''}
            onChange={(e) => setFormData({ ...formData, contract_value: Number(e.target.value) })}
            placeholder="0"
          />
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Trạng thái</label>
            <select
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.status_code || 'draft'}
              onChange={(e) => setFormData({ ...formData, status_code: e.target.value as ContractStatus })}
            >
              {Object.entries(contractStatusMap).map(([val, cfg]) => (
                <option key={val} value={val}>{cfg.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Ghi chú / Mô tả</label>
          <textarea
            className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 min-h-[100px]"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Nhập mô tả chi tiết hợp đồng..."
          />
        </div>

        <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
          <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>
            Hủy
          </Button>
          <Button variant="primary" type="submit" isLoading={isPending}>
            {isEdit ? 'Lưu thay đổi' : 'Khởi tạo hợp đồng'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
